/**
 * API types and interfaces for the Agile Tool Chest frontend
 */

// === Dual Estimate Types ===

/** Enum for the two estimate type slots */
export type EstimateType = 'primary' | 'secondary';

/** A single vote entry in the dual estimate map */
export interface DualEstimateVoteEntry {
  value: number | string;
  votingRound: number;
}

/** Configuration for dual estimate display labels */
export interface DualEstimateConfig {
  primaryLabel: string;
  secondaryLabel: string;
}

// Standard API response wrapper
export interface ApiResponse<T> {
  data: T;
  success: boolean;
  requestId: string;
}

// Room state from API
export interface Room {
  roomKey: string;
  roomName: string;
  subject?: string;  // Estimation poker subject
  estimationMode?: 'single' | 'dual';  // NEW: estimation mode selector (defaults to 'single' if absent for backward compatibility)
  dualEstimateLabels?: DualEstimateConfig;  // NEW: present when estimationMode === 'dual'
  estimationPokerState: {
    votingRound: number;
    votesRevealed: boolean;
  };
  confidenceVoteState: {
    votingRound: number;
    votesRevealed: boolean;
  };
  estimationPokerTimerStartedAt: string | null;  // ISO timestamp when timer was started
  currentServerTime: string;  // ISO timestamp of current server time
  createdAt: string;
  updatedAt: string;
}

// Voter state from API
export interface Voter {
  voterKey: string;
  roomKey: string;
  voterName: string;
  estimationPokerVote?: {
    value: number | string;
    votingRound: number;
  };
  estimationPokerDualVotes?: Partial<Record<EstimateType, DualEstimateVoteEntry>>;  // NEW: dual estimate votes map (partial - voters may not have voted for all types)
  confidenceVote?: {
    value: number;
    votingRound: number;
  };
  activeTab: 'estimation' | 'confidence' | 'retrospective' | null;  // Tab the voter is currently viewing
  createdAt: string;
  updatedAt: string;
}

// Room with voters (API response)
export interface RoomWithVoters {
  room: Room;
  voters: Voter[];
}

// Request payloads
export interface CreateRoomRequest {
  roomName: string;
  estimationMode?: 'single' | 'dual';  // NEW: defaults to 'single'
  dualEstimateLabels?: DualEstimateConfig;  // NEW: optional, used when estimationMode === 'dual'
}

export interface JoinRoomRequest {
  userName: string;
}

export interface UpdateRoomRequest {
  subject: string;
}

export interface SubmitVoteRequest {
  vote: number | string;
  votingRound: number;
  estimateType?: EstimateType;  // NEW: 'primary' | 'secondary' for dual estimate mode
}

export interface UpdateVoterRequest {
  userName?: string;
  activeTab?: 'estimation' | 'confidence' | 'retrospective';
}

// API Error types
export interface ApiError {
  message: string;
  code?: string;
  details?: any;
}

export class ApiClientError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string,
    public details?: any
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

// Voting History types
export interface HistoryEntry {
  subject: string;
  voteDistribution: Record<string, number>;
  dualVoteDistributions?: Record<EstimateType, Record<string, number>>;  // NEW: dual vote distributions keyed by estimate type
  revealedAt: string;
  votingRound: number;
  totalVoters: number;
  timerSeconds: number | null;
}

export interface VotingHistory {
  roomKey: string;
  entries: HistoryEntry[];
}